import React from 'react';
import pick from 'lodash.pick';
import PropTypes from 'prop-types';
import { FAQsJsonLD } from '@tkww/the-bash-frontend';

import MetaDataMarkup from 'components/MetaDataMarkup';
import HelpPage from 'views/HelpPage';
import { gmConfig } from 'helpers/config';
import buildPageAnalytics from 'helpers/buildPageAnalytics';
import { FAQ_PAGE_NAME } from 'constants/pageNames';
import { getFaqCategories } from 'api/faqContent';
import configureStore from 'state/configureStore';
import getServerSidePropsWrapper from 'helpers/getServerSidePropsWrapper';

const HelpCenterPage = ({ faqCategories, defaultCategory, defaultQuestion, defaultGroup }) => {
  const title = 'Frequently Asked Questions';
  const description =
    'Have a question about The Bash? Click here to find frequently asked questions from party planners and answers regarding The Bash and our services.';

  const openGraphProps = {
    description,
    title,
    type: 'website',
    url: `https://${gmConfig.domains.www}/help`,
  };

  const faqSchemaData = faqCategories.reduce(
    (faqs, category) =>
      faqs.concat(
        category.faqGroups.reduce(
          (groupFaqs, group) =>
            groupFaqs.concat(
              group.faqEntries.map((faqEntry) => ({
                question: faqEntry.question,
                answer: faqEntry.answer,
              }))
            ),
          []
        )
      ),
    []
  );

  return (
    <>
      <MetaDataMarkup title={title} description={description} openGraphProps={openGraphProps} />
      <HelpPage
        faqCategories={faqCategories}
        defaultCategory={defaultCategory}
        defaultQuestion={defaultQuestion}
        defaultGroup={defaultGroup}
      />
      <FAQsJsonLD faqs={faqSchemaData} />
    </>
  );
};

const validQueryParams = ['question', 'category', 'group'];

export const handleServerSideProps = async (ctx) => {
  const { query: rawQuery } = ctx;
  const query = pick({ ...rawQuery }, validQueryParams);
  let faqCategories = [];
  let errorCode = null;
  let defaultCategory = null;
  let defaultGroup = null;
  let defaultQuestion = null;

  try {
    faqCategories = await getFaqCategories();
    if (query.question) {
      const foundCategory = faqCategories.find((category) =>
        category.faqGroups.find((faqGroup) => {
          const questionResult = faqGroup.faqEntries.find((entry) => entry.slug === query.question);
          if (questionResult) {
            defaultQuestion = questionResult.slug;
            return true;
          }
          return false;
        })
      );
      if (foundCategory) {
        defaultCategory = foundCategory.slug;
      }
    } else if (query.group) {
      const foundCategory = faqCategories.find((category) => {
        const groupResult = category.faqGroups.find((faqGroup) => faqGroup.slug === query.group);
        if (groupResult) {
          defaultGroup = groupResult.slug;
          return true;
        }
        return false;
      });

      if (foundCategory) {
        defaultCategory = foundCategory.slug;
      }
    } else if (query.category) {
      const foundCategory = faqCategories.find((category) => category.slug === query.category);
      if (foundCategory) {
        defaultCategory = foundCategory.slug;
      }
    }

    if (!defaultCategory) {
      defaultCategory = faqCategories[0].slug;
    }
  } catch (err) {
    errorCode = err?.statusCode || 500;
  }

  const pageAnalytics = buildPageAnalytics(FAQ_PAGE_NAME);

  return {
    ...pageAnalytics,
    errorCode,
    faqCategories,
    defaultCategory,
    defaultGroup,
    defaultQuestion,
  };
};

export const getServerSideProps = configureStore.getServerSideProps(
  (store) => async (ctx) =>
    await getServerSidePropsWrapper({ ...ctx, store }, handleServerSideProps)
);

HelpCenterPage.propTypes = {
  faqCategories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  defaultCategory: PropTypes.string.isRequired,
  defaultGroup: PropTypes.string,
  defaultQuestion: PropTypes.string,
};

HelpCenterPage.defaultProps = {
  defaultGroup: null,
  defaultQuestion: null,
};

export default HelpCenterPage;
